import { useSnackbar } from "notistack";
import useDialog from "@dyl/hooks/useDialog";
import { useCallback } from "react";
import { Button, CircularProgress, DialogContentText } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import createSnackbarAction from "@dyl/utils/createSnackbarAction";
import { noop } from "lodash";

const useStyles = makeStyles(theme => ({
    root: {
        color: theme.palette.error.main,
    },
}));

// All keys should be template literals
const _REQUIRED_KEYS = [
    'dialogTitle', 'dialogText', 'ongoingNotification',
    'successNotification', 'failureNotification', 'errorNotification'
];

export default function GenericDeleteAction({ data, deleteAction, onDelete = noop, responseSuccessPredicate = null, promptTexts = null, ...other }) {
    console.assert(!!data, 'data is required.');
    console.assert(!!responseSuccessPredicate, 'responseSuccessPredicate is required.');
    console.assert(
        !!promptTexts && _REQUIRED_KEYS.every(key => key in promptTexts),
        `promptTexts must exist and contain all necessary keys: ${_REQUIRED_KEYS}`
    );

    const {
        dialogTitle, dialogText, ongoingNotification,
        successNotification, failureNotification, errorNotification
    } = promptTexts;

    const classes = useStyles();

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [ImperativeDialog, openDialog] = useDialog();

    const onClick = useCallback(
        /** @param {MouseEvent} event */
        async event => {
            event.stopPropagation();
            event.preventDefault();
            const text = dialogText(data);
            const confirmed = await new Promise(resolve => openDialog(
                dialogTitle(data),
                <>
                    { (Array.isArray((text)) ? text : [text]).map((t, i) =>
                        <DialogContentText key={i}>
                            {t}
                        </DialogContentText>
                    )}
                </>,
                [
                    { text: 'Cancel', onClick: () => resolve(false) },
                    { text: 'Delete', onClick: () => resolve(true), destructive: true },
                ],
            ));

            if (!confirmed) return;

            const indicator = enqueueSnackbar(ongoingNotification(data), {
                variant: 'info',
                persist: true,
                action: createSnackbarAction({
                    iconComponent: () => <CircularProgress size='1em' color='inherit' />,
                }),
            });

            try {
                const response = await deleteAction(data);

                if (responseSuccessPredicate(response)) {
                    enqueueSnackbar(successNotification(data, response), { variant: 'success' });
                    onDelete();
                } else {
                    enqueueSnackbar(failureNotification(data, response), { variant: 'error' });
                }
            }
            catch {
                enqueueSnackbar(errorNotification(data), { variant: 'error' });
            }
            finally {
                closeSnackbar(indicator);
            }
        },
        [data]
    )

    return (
        <div onClick={(event) => event?.stopPropagation?.()}>
            <Button
                className={classes.root}
                onClick={onClick}
                {...other}
            >
                Delete
            </Button>

            <ImperativeDialog />
        </div>
    );
}